import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import {
  Eye, EyeOff, Mail, Lock, Building2, ArrowRight,
  MessageCircle, FileSearch, Shield, ChevronRight,
  FileText, CreditCard, Zap, CheckCircle, Cpu, Database
} from 'lucide-react';

// ─── THREE.JS CANVAS (dekorativ, vollständiger Fallback wenn nicht verfügbar) ─
const ThreeBackground = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !THREE) return;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    } catch {
      return;
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(canvas.offsetWidth, canvas.offsetHeight);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      canvas.offsetWidth / canvas.offsetHeight,
      0.1,
      100
    );
    camera.position.z = 4;

    const isMobile = window.innerWidth < 768;
    const pointCount = isMobile ? 50 : 130;
    const posArr = new Float32Array(pointCount * 3);
    const pointData = [];

    for (let i = 0; i < pointCount; i++) {
      const x = (Math.random() - 0.5) * 10;
      const y = (Math.random() - 0.5) * 7;
      const z = (Math.random() - 0.5) * 4;
      posArr[i * 3]     = x;
      posArr[i * 3 + 1] = y;
      posArr[i * 3 + 2] = z;
      pointData.push({
        x, y, z,
        vx: (Math.random() - 0.5) * 0.003,
        vy: (Math.random() - 0.5) * 0.003,
      });
    }

    const pointGeo = new THREE.BufferGeometry();
    pointGeo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    const pointMat = new THREE.PointsMaterial({
      color: 0x818cf8,
      size: 0.05,
      transparent: true,
      opacity: 0.65,
    });
    const points = new THREE.Points(pointGeo, pointMat);
    scene.add(points);

    // Verbindungslinien
    const linePos = [];
    for (let i = 0; i < pointCount; i++) {
      for (let j = i + 1; j < pointCount; j++) {
        const dx = pointData[i].x - pointData[j].x;
        const dy = pointData[i].y - pointData[j].y;
        const dz = pointData[i].z - pointData[j].z;
        if (Math.sqrt(dx * dx + dy * dy + dz * dz) < 2.0) {
          linePos.push(
            pointData[i].x, pointData[i].y, pointData[i].z,
            pointData[j].x, pointData[j].y, pointData[j].z
          );
        }
      }
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(linePos), 3));
    const lineMat = new THREE.LineBasicMaterial({
      color: 0x6366f1,
      transparent: true,
      opacity: 0.12,
    });
    scene.add(new THREE.LineSegments(lineGeo, lineMat));

    // Dezenter Zentralknoten
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(0.15, 16, 16),
      new THREE.MeshBasicMaterial({
        color: 0x818cf8,
        wireframe: true,
        transparent: true,
        opacity: 0.15,
      })
    );
    scene.add(sphere);

    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      const pos = pointGeo.attributes.position.array;
      for (let i = 0; i < pointCount; i++) {
        pointData[i].x += pointData[i].vx;
        pointData[i].y += pointData[i].vy;
        if (Math.abs(pointData[i].x) > 5) pointData[i].vx *= -1;
        if (Math.abs(pointData[i].y) > 3.5) pointData[i].vy *= -1;
        pos[i * 3]     = pointData[i].x;
        pos[i * 3 + 1] = pointData[i].y;
      }
      pointGeo.attributes.position.needsUpdate = true;
      sphere.rotation.y += 0.002;
      sphere.rotation.x += 0.001;
      points.rotation.y += 0.0004;
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!canvas) return;
      renderer.setSize(canvas.offsetWidth, canvas.offsetHeight);
      camera.aspect = canvas.offsetWidth / canvas.offsetHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
      pointGeo.dispose();
      lineGeo.dispose();
      pointMat.dispose();
      lineMat.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.5 }}
    />
  );
};

// ─── HAUPTKOMPONENTE ─────────────────────────────────────────────────────────
const LoginPage = () => {
  const { signIn, isAuthenticated, isLoading } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Accordion: erste Karte standardmäßig geöffnet
  const [openModules, setOpenModules] = useState(() => new Set(['Dokumente & KI-Analyse']));
  const toggleModule = (title) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      if (next.has(title)) { next.delete(title); } else { next.add(title); }
      return next;
    });
  };

  // Progressive Disclosure
  const hasAccount = typeof window !== 'undefined' && localStorage.getItem('clara_has_account') === 'true';
  const forceLogin = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('login') === '1';
  const showLoginProminent = hasAccount || forceLogin;

  // Sticky Header Scroll-Listener
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    try {
      const result = await signIn(formData.email, formData.password);
      if (!result.success) {
        setLoginError(result.error || 'Login fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.');
      } else {
        // clara_has_account erst nach erfolgreichem Login setzen
        localStorage.setItem('clara_has_account', 'true');
      }
    } catch {
      setLoginError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const modules = [
    {
      icon: FileText,
      accent: 'border-blue-500/25 hover:border-blue-400/50',
      iconColor: 'text-blue-400',
      iconBg: 'bg-blue-500/15',
      title: 'Dokumente & KI-Analyse',
      desc: 'Jedes Dokument erkannt, analysiert und strukturiert. OCR, Vertragsanalyse, automatische Klassifikation — vom Mietvertrag bis zur Rechnung.',
    },
    {
      icon: CheckCircle,
      accent: 'border-emerald-500/25 hover:border-emerald-400/50',
      iconColor: 'text-emerald-400',
      iconBg: 'bg-emerald-500/15',
      title: 'Mieteingangskontrolle',
      desc: 'Automatische Überwachung aller Mieteingänge. Rückstände werden sofort erkannt — keine manuelle Prüfung mehr.',
    },
    {
      icon: MessageCircle,
      accent: 'border-violet-500/25 hover:border-violet-400/50',
      iconColor: 'text-violet-400',
      iconBg: 'bg-violet-500/15',
      title: 'WhatsApp & Kommunikation',
      desc: 'Mieter schreiben per WhatsApp. Clara klassifiziert, antwortet und eskaliert — alles in einem strukturierten Kommunikations-Hub.',
    },
    {
      icon: CreditCard,
      accent: 'border-cyan-500/25 hover:border-cyan-400/50',
      iconColor: 'text-cyan-400',
      iconBg: 'bg-cyan-500/15',
      title: 'Banking & Buchungen',
      desc: 'Transaktionen automatisch zugeordnet. Cashflow, offene Posten und Finanzsignale auf einen Blick — ohne manuelle Buchführung.',
    },
    {
      icon: Building2,
      accent: 'border-amber-500/25 hover:border-amber-400/50',
      iconColor: 'text-amber-400',
      iconBg: 'bg-amber-500/15',
      title: 'Nebenkostenabrechnung',
      desc: 'NK-Abrechnung automatisiert. Umlagefähigkeit geprüft, Mieteranteile berechnet — gesetzeskonform und nachvollziehbar.',
    },
    {
      icon: Zap,
      accent: 'border-rose-500/25 hover:border-rose-400/50',
      iconColor: 'text-rose-400',
      iconBg: 'bg-rose-500/15',
      title: 'Software-Migration',
      desc: 'Wechsel von Domus, Immoware24, WinCasa, iX-Haus, Wodis oder Excel? Export hochladen — Clara erkennt das Format und übernimmt alles.',
    },
  ];

  const ctaCards = [
    {
      icon: Building2,
      accent: 'from-blue-500/15 to-blue-600/5 border-blue-500/25 hover:border-blue-400/50',
      iconColor: 'text-blue-400',
      iconBg: 'bg-blue-500/15',
      title: 'Bestand digitalisieren',
      desc: 'Objekte, Mietverträge und Unterlagen strukturiert zu Clara360 überführen.',
      cta: 'Zum Onboarding',
      ctaColor: 'text-blue-400',
      href: '/pilot',
      external: true,
    },
    {
      icon: FileSearch,
      accent: 'from-violet-500/15 to-violet-600/5 border-violet-500/25 hover:border-violet-400/50',
      iconColor: 'text-violet-400',
      iconBg: 'bg-violet-500/15',
      title: 'Dokument prüfen lassen',
      desc: 'Mietvertrag, NK-Abrechnung, Mahnung oder Rechnung — Clara analysiert und gibt strukturiertes Feedback.',
      cta: 'Dokument hochladen',
      ctaColor: 'text-violet-400',
      href: '/pilot',
      external: false,
    },
    {
      icon: MessageCircle,
      accent: 'from-emerald-500/15 to-emerald-600/5 border-emerald-500/25 hover:border-emerald-400/50',
      iconColor: 'text-emerald-400',
      iconBg: 'bg-emerald-500/15',
      title: 'Mit Clara starten',
      desc: 'Anliegen per WhatsApp schildern. Clara klassifiziert, fragt nach und bereitet den nächsten Schritt vor.',
      cta: 'Per WhatsApp schreiben',
      ctaColor: 'text-emerald-400',
      href: 'https://wa.me/491709782676?text=Hallo%20Clara%2C%20ich%20m%C3%B6chte%20meinen%20Immobilienbestand%20digitalisieren.',
      external: true,
    },
  ];

  const cards = [
    {
      icon: Building2,
      accent: 'from-blue-500/15 to-blue-600/5 border-blue-500/25 hover:border-blue-400/50',
      iconColor: 'text-blue-400',
      iconBg: 'bg-blue-500/15',
      title: 'Bestand digitalisieren',
      desc: 'Objekte, Mietverträge und Unterlagen strukturiert zu Clara360 überführen. Clara begleitet jeden Schritt.',
      cta: 'Zum Onboarding',
      ctaColor: 'text-blue-400',
      href: '/pilot',
      external: true,
    },
    {
      icon: FileSearch,
      accent: 'from-violet-500/15 to-violet-600/5 border-violet-500/25 hover:border-violet-400/50',
      iconColor: 'text-violet-400',
      iconBg: 'bg-violet-500/15',
      title: 'Dokument prüfen lassen',
      desc: 'Mietvertrag, Nebenkostenabrechnung, Mahnung oder Rechnung — Clara analysiert und gibt strukturiertes Feedback.',
      cta: 'Dokument hochladen',
      ctaColor: 'text-violet-400',
      href: '/api/analyze-document',
      external: false,
    },
    {
      icon: MessageCircle,
      accent: 'from-emerald-500/15 to-emerald-600/5 border-emerald-500/25 hover:border-emerald-400/50',
      iconColor: 'text-emerald-400',
      iconBg: 'bg-emerald-500/15',
      title: 'Mit Clara starten',
      desc: 'Anliegen per Chat oder WhatsApp schildern. Clara klassifiziert, fragt nach und bereitet den nächsten Schritt vor.',
      cta: 'Per WhatsApp schreiben',
      ctaColor: 'text-emerald-400',
      href: 'https://wa.me/491709782676?text=Hallo%20Clara%2C%20ich%20m%C3%B6chte%20meinen%20Immobilienbestand%20digitalisieren.',
      external: true,
    },
  ];

  return (
    /* ── Vollbild-Hintergrund: einheitlicher Verlauf, kein Schnitt ── */
    <div
      className="min-h-screen overflow-hidden relative flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 35%, #1a1040 60%, #0f0a2e 100%)',
      }}
    >
      {/* ── Weiche Licht-Blobs über gesamte Fläche ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[55%] h-[70%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-10%] right-[-5%] w-[50%] h-[60%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 70%)' }} />
        <div className="absolute top-[30%] left-[40%] w-[40%] h-[50%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)' }} />
      </div>

      {/* ── Three.js Canvas über gesamte Fläche ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <ThreeBackground />
      </div>

      {/* ── Sticky Header ── */}
      <div
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: isScrolled ? 'rgba(15,23,42,0.92)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: isScrolled ? 'blur(16px)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(255,255,255,0.07)' : 'none',
        }}
      >
        <div className="flex items-center justify-between px-6 md:px-12 py-3">
          <div className="flex items-center gap-2.5">
            <img
              src="/clara360-logo.png"
              alt="Clara360"
              style={{ height: '28px', width: 'auto', display: 'block' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="text-white font-bold text-sm tracking-tight">Clara360</span>
          </div>
          <div className="flex items-center gap-3">
            {showLoginProminent ? (
              <a href="#login" className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-lg transition-colors">
                Anmelden
              </a>
            ) : (
              <>
                <a href="#login" className="text-gray-400 hover:text-white text-xs transition-colors hidden sm:block">
                  Bereits Kunde? Anmelden
                </a>
                <Link
                  to="/register"
                  className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  Jetzt starten
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Spacer für Sticky Header */}
      <div className="h-14" />

      {/* ── Haupt-Inhalt: zentriert ── */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-8 md:py-10">

        {/* Headline-Block */}
        <div className="text-center mb-6 md:mb-10 max-w-3xl relative">
          <p className="text-indigo-400 text-xs font-semibold uppercase tracking-widest mb-3">Operatives Cockpit für Immobilienverwaltung</p>
          <h1 className="text-2xl md:text-5xl font-bold text-white leading-tight mb-4">
            Clara360 ist kein Chatbot<br className="hidden md:block" /> für Hausverwaltung.
          </h1>
          <p className="text-base md:text-xl font-medium mb-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Ein KI-gestütztes Betriebssystem für Bestand, Dokumente, Finanzen und Kommunikation.
          </p>
          <p className="text-gray-500 text-sm">Clara empfiehlt. Sie entscheiden. Immer.</p>

          {/* ── Datenfluss Overlay-Chips (nur Desktop, prefers-reduced-motion respektiert) ── */}
          <div className="hidden md:flex absolute inset-0 pointer-events-none items-center justify-between px-0" aria-hidden="true">
            {[{label:'Dokumente',color:'rgba(99,102,241,0.7)',x:'-120px'},{label:'Mieten',color:'rgba(16,185,129,0.7)',x:'0'},{label:'WhatsApp',color:'rgba(139,92,246,0.7)',x:'120px'}].map(({label,color})=>(
              <span key={label} className="text-xs font-medium px-2.5 py-1 rounded-full border" style={{
                color, borderColor: color.replace('0.7','0.4'),
                background: color.replace('0.7','0.08'),
                animation: 'pulse 3s ease-in-out infinite',
              }}>{label} →</span>
            ))}
          </div>
        </div>

        {/* CTA-Karten für neue Besucher zuerst (Zustand A: kein Account, kein ?login=1) */}
        {!showLoginProminent && (
          <div className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {ctaCards.map(({ icon: Icon, accent, iconColor, iconBg, title, desc, cta, ctaColor, href, external }) => (
              <a
                key={title}
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className={`group flex flex-col p-4 rounded-2xl bg-gradient-to-br ${accent} border backdrop-blur-sm transition-all duration-300 cursor-pointer no-underline`}
              >
                <div className={`w-8 h-8 ${iconBg} rounded-xl flex items-center justify-center mb-2 transition-transform group-hover:scale-110`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <h3 className="text-white font-semibold text-xs mb-1">{title}</h3>
                <p className="text-gray-400 text-xs flex-1 leading-relaxed">{desc}</p>
                <div className={`mt-2 flex items-center gap-1 ${ctaColor} text-xs font-medium group-hover:gap-2 transition-all`}>
                  {cta} <ChevronRight className="w-3 h-3" />
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Login-Box: Glassmorphism, zentriert */}
        <div
          id="login"
          className="w-full max-w-md rounded-3xl p-6 md:p-8 mb-8 shadow-2xl"
          style={{
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 8px 64px rgba(99,102,241,0.18), 0 2px 16px rgba(0,0,0,0.4)',
          }}
        >
          <div className="mb-7">
            <h2 className="text-xl font-bold text-white mb-1">Willkommen zurück</h2>
            <p className="text-gray-400 text-sm">Melden Sie sich in Ihrem Clara360-Konto an</p>
          </div>

          {loginError && (
            <div className="mb-4 p-3 bg-red-900/40 border border-red-700/50 rounded-xl text-red-300 text-sm">
              {loginError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                E-Mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ihre@email.de"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-indigo-500/70 focus:ring-1 focus:ring-indigo-500/30 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                Passwort
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className="w-full pl-10 pr-12 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-indigo-500/70 focus:ring-1 focus:ring-indigo-500/30 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoggingIn || isLoading}
              className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 disabled:from-indigo-800 disabled:to-violet-900 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-900/40"
            >
              {isLoggingIn ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Anmelden...
                </span>
              ) : (
                <>Anmelden <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/8">
            <p className="text-center text-xs text-gray-600">
              Noch kein Konto?{' '}
              <Link
                to="/register"
                className="text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Jetzt starten
              </Link>
            </p>
          </div>
        </div>

        {/* ── 6 Nutzenmodule ── */}
        <div className="w-full max-w-4xl mb-6 md:mb-10">
          <h2 className="text-center text-white font-semibold text-base md:text-lg mb-4 md:mb-6">Was Clara360 löst</h2>

          {/* Desktop: 3-spaltiges Grid mit vollem Text */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {modules.map(({ icon: Icon, accent, iconColor, iconBg, title, desc }) => (
              <div
                key={title}
                className={`flex flex-col p-5 rounded-2xl border ${accent} transition-all duration-300`}
                style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(8px)' }}
              >
                <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${iconColor}`} />
                </div>
                <h3 className="text-white font-semibold text-sm mb-2">{title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile: Accordion */}
          <div className="md:hidden flex flex-col gap-2">
            {modules.map(({ icon: Icon, accent, iconColor, iconBg, title, desc }) => {
              const isOpen = openModules.has(title);
              return (
                <div
                  key={title}
                  className={`rounded-xl border ${accent} transition-all duration-300`}
                  style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(8px)' }}
                >
                  <button
                    onClick={() => toggleModule(title)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between p-3 text-left"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                      </div>
                      <span className="text-white font-semibold text-xs">{title}</span>
                    </div>
                    <ChevronRight
                      className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                    />
                  </button>
                  <div
                    style={{
                      maxHeight: isOpen ? '120px' : '0',
                      opacity: isOpen ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.25s ease, opacity 0.2s ease',
                    }}
                  >
                    <p className="text-gray-400 text-xs leading-relaxed px-3 pb-3">{desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Governance-Block — drei Säulen ── */}
        <div className="w-full max-w-4xl mb-6 md:mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Säule 1 */}
            <div
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)' }}
            >
              <div className="w-9 h-9 bg-indigo-500/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="w-4 h-4 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm mb-1">Keine Aktion ohne Freigabe</h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Clara analysiert, strukturiert und empfiehlt. Sie entscheiden, bevor Daten übernommen, geändert oder weiterverarbeitet werden.
                </p>
              </div>
            </div>
            {/* Säule 2 */}
            <div
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.18)' }}
            >
              <div className="w-9 h-9 bg-violet-500/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Cpu className="w-4 h-4 text-violet-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm mb-1">Modelloffen</h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Clara360 ist nicht an ein einzelnes KI-Modell gebunden. Lokale, hybride oder cloudbasierte Verarbeitung kann je nach Aufgabe kontrolliert eingesetzt werden.
                </p>
              </div>
            </div>
            {/* Säule 3 */}
            <div
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)' }}
            >
              <div className="w-9 h-9 bg-blue-500/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Database className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm mb-1">Daten kontrolliert</h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Sensible Verwaltungsdaten werden nach Sensibilität behandelt, reduziert, kontextualisiert oder bevorzugt lokal verarbeitet.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Drei CTA-Karten (nur bei bekannten Nutzern, da neue Besucher sie oben sehen) ── */}
        {showLoginProminent && (
          <div className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {ctaCards.map(({ icon: Icon, accent, iconColor, iconBg, title, desc, cta, ctaColor, href, external }) => (
              <a
                key={title}
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className={`group flex flex-col p-4 rounded-2xl bg-gradient-to-br ${accent} border backdrop-blur-sm transition-all duration-300 cursor-pointer no-underline`}
              >
                <div className={`w-8 h-8 ${iconBg} rounded-xl flex items-center justify-center mb-2 transition-transform group-hover:scale-110`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <h3 className="text-white font-semibold text-xs mb-1">{title}</h3>
                <p className="text-gray-400 text-xs flex-1 leading-relaxed">{desc}</p>
                <div className={`mt-2 flex items-center gap-1 ${ctaColor} text-xs font-medium group-hover:gap-2 transition-all`}>
                  {cta} <ChevronRight className="w-3 h-3" />
                </div>
              </a>
            ))}
          </div>
        )}

        {/* ── Modul-Hinweis ── */}
        <p className="text-gray-600 text-xs text-center mb-4">
          Weitere Module wie Steuern, WEG-Management, Investment-Cockpit und Finanzierungen stehen nach Login zur Verfügung.
        </p>

        {/* Vertrauens-Signal */}
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <p className="text-gray-600 text-xs text-center">
            Pilotbetrieb · Keine Rechts- oder Steuerberatung · Datenschutz
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
